// Judge.me's new review widget (.jm-*), which every newly installed shop gets. sw-pdp.js only knows the
// old widget (.jdgm-rev-widg), so this adds the same card, star bars and filter chips around the new one.
// The widget re-renders itself, so its nodes stay where they are: sw-pdp-reviews-jm.css lays them out and
// the controls here only press its hidden buttons.
(() => {
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  function readStats(block) {
    const text = block.querySelector('script.jdgm-review-widget-data')?.textContent || '';
    let histogram = [];
    try {
      histogram = JSON.parse((text.match(/"histogram":(\[.*?\])/) || [])[1] || '[]');
    } catch (e) {
      histogram = [];
    }
    const count = histogram.reduce((sum, row) => sum + row.frequency, 0);
    const average = parseFloat((text.match(/"average_rating":"?([\d.]+)/) || [])[1]) || 0;
    return { histogram, count, average };
  }

  function buildBars(histogram) {
    const bars = el('div', 'jdgm-histogram');
    histogram.forEach((row) => {
      const line = el('div', 'jdgm-histogram__row');
      line.dataset.rating = row.rating;
      line.dataset.percentage = row.percentage;
      const bar = el('div', 'jdgm-histogram__bar');
      const fill = el('div', 'jdgm-histogram__bar-content');
      fill.style.width = `${row.percentage}%`;
      bar.append(fill);
      line.append(bar);
      bars.append(line);
    });
    return bars;
  }

  function buildCard(stats, labels, widget) {
    const card = el('div', 'sw-rv-card');
    card.append(el('div', 'sw-rv-card__title', labels.itemTitle));
    const score = el('div', 'sw-rv-score');
    score.append(el('span', 'sw-rv-score__avg', stats.average.toFixed(1)), el('hr', 'sw-rv-score__divider'));
    const right = el('div');
    const stars = el('div', 'sw-rv-stars');
    stars.style.setProperty('--sw-rating', stats.average);
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${stats.average.toFixed(1)} / 5`);
    const summary = stats.count ? `${stats.count} Review${stats.count === 1 ? '' : 's'}` : 'No reviews yet';
    right.append(stars, el('span', 'sw-rv-score__count', summary));
    score.append(right);
    card.append(score);
    if (stats.count) card.append(buildBars(stats.histogram));
    else card.classList.add('sw-rv-card--empty');
    const write = el('button', 'sw-jm-write', 'Write a review');
    write.type = 'button';
    write.addEventListener('click', () => widget.querySelector('.jm-action-buttons__button')?.click());
    card.append(write);
    return card;
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // The widget ignores a filter change while it is still loading the previous one.
  async function whenIdle(widget) {
    for (let i = 0; i < 60; i += 1) {
      await sleep(50);
      if (!widget.querySelector('.jm-loading-overlay--active')) return;
    }
  }

  // The rating filter is a set of checkboxes that only exist once its panel has been opened.
  async function filterByRating(widget, rating) {
    if (!widget.querySelector('input[name="rating-filter"]')) {
      widget.querySelector('.jm-custom-answer-filter')?.click();
      await sleep(100);
    }
    const inputs = [...widget.querySelectorAll('input[name="rating-filter"]')];
    const changes = inputs.filter((input) => input.checked !== (input.value === String(rating)));
    changes.sort((a, b) => b.checked - a.checked);
    for (const input of changes) {
      input.click();
      await whenIdle(widget);
    }
  }

  function buildChips(stats, labels, widget) {
    const chips = el('div', 'sw-rv-chips');
    const add = (label, count, rating) => {
      const chip = el('button', 'sw-rv-chip');
      chip.type = 'button';
      chip.append(document.createTextNode(label), el('span', 'sw-rv-chip__count', `(${count})`));
      chip.disabled = count === 0;
      chip.addEventListener('click', () => {
        chips.querySelectorAll('.sw-rv-chip').forEach((c) => c.classList.toggle('is-active', c === chip));
        filterByRating(widget, rating);
      });
      chips.append(chip);
      return chip;
    };
    add(labels.allLabel, stats.count, null).classList.add('is-active');
    stats.histogram.forEach((row) => add(`${row.rating} Star`, row.frequency, row.rating));
    return chips;
  }

  function build(section, widget) {
    const block = widget.closest('.shopify-app-block');
    if (!block || block.classList.contains('sw-jm')) return;
    block.classList.add('sw-jm');
    const labels = section.dataset;
    const stats = readStats(block);
    const imagesTitle = el('h3', 'sw-rv-heading sw-jm-images-title', labels.imagesTitle);
    const listHead = el('div', 'sw-rv-list-head');
    listHead.append(el('h3', 'sw-rv-heading', labels.listTitle), buildChips(stats, labels, widget));
    listHead.hidden = stats.count === 0;
    block.prepend(buildCard(stats, labels, widget), imagesTitle, listHead);
    const syncImages = () => {
      imagesTitle.hidden = !widget.querySelector('.jm-media-grid__item');
    };
    syncImages();
    new MutationObserver(syncImages).observe(widget, { childList: true, subtree: true });
  }

  function init(section) {
    const attempt = () => {
      const widget = section.querySelector('.jm-review-widget');
      if (widget) build(section, widget);
      return Boolean(widget);
    };
    if (attempt()) return;
    const watcher = new MutationObserver(() => {
      if (attempt()) watcher.disconnect();
    });
    watcher.observe(section, { childList: true, subtree: true });
  }

  document.querySelectorAll('[data-sw-reviews]').forEach(init);
})();
